import { useEffect, useState } from 'react';
import { Input } from '@/components/ui';
import { useVendorsQuery, type VendorOption } from '../hooks/useVendorsQuery';

const DEBOUNCE_MS = 250;

export interface VendorSelectorProps {
  value: string | null;
  disabled?: boolean;
  errorMessage?: string | null;
  onChange: (vendorId: string | null) => void;
  placeholder?: string;
}

export const VendorSelector = ({
  value,
  disabled,
  errorMessage,
  onChange,
  placeholder = '벤더 검색...',
}: VendorSelectorProps) => {
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (keyword === debouncedKeyword) return;
    const id = setTimeout(() => setDebouncedKeyword(keyword), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [keyword, debouncedKeyword]);

  const listQuery = useVendorsQuery({
    size: 10,
    ...(debouncedKeyword.trim() ? { keyword: debouncedKeyword.trim() } : {}),
  });

  const items = listQuery.data ?? [];

  const selectedItem = value
    ? (items.find((v) => v.vendorId === value) ?? null)
    : null;

  const selectedName = selectedItem?.vendorName ?? (value ? value : null);

  const handleSelect = (vendor: VendorOption) => {
    onChange(vendor.vendorId);
    setOpen(false);
    setKeyword('');
  };

  const handleClear = () => {
    onChange(null);
    setKeyword('');
  };

  return (
    <div className="flex flex-col gap-1" data-testid="vendor-selector">
      {selectedName ? (
        <div
          className="flex items-center justify-between rounded-md border px-3 py-2"
          style={{ borderColor: 'var(--content-muted)', background: 'var(--surface-0)' }}
        >
          <span className="text-sm font-medium text-[color:var(--content-strong)]">
            {selectedName}
          </span>
          <button
            type="button"
            onClick={handleClear}
            disabled={disabled}
            className="text-xs text-[color:var(--content-muted)] hover:text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
            aria-label="벤더 선택 해제"
          >
            ×
          </button>
        </div>
      ) : (
        <Input
          id="vendor-selector-input"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={placeholder}
          disabled={disabled}
          style={{ height: 'var(--density-compact-form-h)' }}
        />
      )}

      {open && !selectedName ? (
        <ul
          role="listbox"
          aria-label="벤더 목록"
          className="z-10 max-h-56 overflow-y-auto rounded-md border"
          style={{
            borderColor: 'var(--content-muted)',
            background: 'var(--surface-raised)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {listQuery.isPending ? (
            <li className="px-3 py-2 text-xs text-[color:var(--content-muted)]">로딩 중...</li>
          ) : listQuery.isError ? (
            <li className="px-3 py-2 text-xs" style={{ color: 'var(--signal-danger)' }}>
              벤더 목록을 불러오지 못했습니다
            </li>
          ) : items.length === 0 ? (
            <li className="px-3 py-2 text-xs text-[color:var(--content-muted)]">
              {debouncedKeyword ? '검색 결과 없음' : '등록된 벤더가 없습니다'}
            </li>
          ) : (
            items.map((v) => (
              <li key={v.vendorId}>
                <button
                  type="button"
                  className="flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-[color:var(--surface-1)] focus-visible:outline-none focus-visible:bg-[color:var(--surface-1)]"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(v)}
                >
                  <span className="font-medium text-[color:var(--content-strong)]">
                    {v.vendorName}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {errorMessage ? (
        <span className="text-xs" style={{ color: 'var(--signal-danger)' }}>
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
};
